import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Chip, Stack, Button, Checkbox, Slider,
  Divider, IconButton, TextField, ToggleButtonGroup, ToggleButton, Dialog,
  DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { useApp } from '../contexts/AppContext';
import { useQuiz } from '../contexts/QuizContext';
import type { QuizConfig } from '../types';

type ModeOption = 'default' | 'exam' | 'homework' | 'halfExam';

const MODE_LABELS: Record<ModeOption, string> = {
  default: '默认', exam: '考试', homework: '作业', halfExam: '半考试',
};

// Checkbox state: unchecked = bank excluded, checked = bank included
// Chapter chips: if any selected = use only those; if none selected = use all from this bank
export default function CustomQuiz() {
  const navigate = useNavigate();
  const { state } = useApp();
  const { startCustomQuiz, session, clearSession } = useQuiz();
  const hasActiveSession = !!(session && !session.isFinished);

  // selectedBanks: banks that are checked (included)
  const [selectedBanks, setSelectedBanks] = useState<Set<string>>(new Set());
  // selectedGroups: per-bank chapter filter. empty array = all chapters from that bank
  const [selectedGroups, setSelectedGroups] = useState<Record<string, string[]>>({});
  const [count, setCount] = useState(20);
  const [mode, setMode] = useState<ModeOption>('default');
  const [shuffleQuestions, setShuffleQuestions] = useState(state.settings.shuffleQuestions);
  const [shuffleOptions, setShuffleOptions] = useState(state.settings.shuffleOptions);
  const [showSessionDialog, setShowSessionDialog] = useState(false);

  const bankGroups = useMemo(() => state.banks.map(bank => ({
    bankId: bank.id,
    bankName: bank.name,
    questionCount: bank.hqb.questions.length,
    groups: bank.hqb.groups.length > 0 ? bank.hqb.groups.sort((a, b) => a.order - b.order) : [],
  })), [state.banks]);

  // Compute total available questions: for each selected bank, count selected groups or all
  const totalAvailable = useMemo(() => {
    let total = 0;
    for (const bg of bankGroups) {
      if (!selectedBanks.has(bg.bankId)) continue;
      const sel = selectedGroups[bg.bankId];
      if (!sel || sel.length === 0) {
        total += bg.questionCount;
      } else {
        const groupIds = new Set(sel);
        const bank = state.banks.find(b => b.id === bg.bankId);
        if (bank) {
          const qIds = new Set(bg.groups.filter(g => groupIds.has(g.id)).flatMap(g => g.questionIds));
          total += bank.hqb.questions.filter(q => qIds.has(q.id)).length;
        }
      }
    }
    return total;
  }, [bankGroups, selectedBanks, selectedGroups, state.banks]);

  // Total questions for display in header
  const totalQuestionsAll = useMemo(() => state.banks.reduce((s, b) => s + b.hqb.questions.length, 0), [state.banks]);

  const doStart = () => {
    const selections = bankGroups
      .filter(bg => selectedBanks.has(bg.bankId))
      .map(bg => ({
        bankId: bg.bankId,
        groupIds: selectedGroups[bg.bankId]?.length ? selectedGroups[bg.bankId] : undefined,
      }));
    if (selections.length === 0) return;
    const config: QuizConfig = {
      selections,
      count,
      mode: mode === 'default' ? undefined : mode,
      shuffleQuestions,
      shuffleOptions,
    };
    startCustomQuiz(config);
    navigate('/quiz/active');
  };

  const handleStart = () => {
    if (hasActiveSession) { setShowSessionDialog(true); return; }
    doStart();
  };

  const toggleBank = (bankId: string) => {
    setSelectedBanks(prev => {
      const next = new Set(prev);
      if (next.has(bankId)) {
        next.delete(bankId);
        // Also clear chapter selection for this bank
        setSelectedGroups(prev2 => {
          const n2 = { ...prev2 };
          delete n2[bankId];
          return n2;
        });
      } else {
        next.add(bankId);
        // Clear chapter filter so whole bank is used
        setSelectedGroups(prev2 => {
          const n2 = { ...prev2 };
          delete n2[bankId];
          return n2;
        });
      }
      return next;
    });
  };

  const toggleGroup = (bankId: string, groupId: string) => {
    if (!selectedBanks.has(bankId)) {
      // If bank not selected yet, checking a chapter auto-selects the bank
      setSelectedBanks(prev => new Set(prev).add(bankId));
      setSelectedGroups(prev => ({ ...prev, [bankId]: [groupId] }));
      return;
    }
    setSelectedGroups(prev => {
      const current = prev[bankId] || [];
      const alreadySelected = current.includes(groupId);
      let next: string[];
      if (alreadySelected) {
        next = current.filter(g => g !== groupId);
        // If none left, clear filter (use all chapters from this bank)
        if (next.length === 0) {
          const n2 = { ...prev };
          delete n2[bankId];
          return n2;
        }
      } else {
        next = [...current, groupId];
      }
      return { ...prev, [bankId]: next };
    });
  };

  const handleSelectAll = () => {
    setSelectedBanks(new Set(bankGroups.map(b => b.bankId)));
    // Clear all chapter filters
    setSelectedGroups({});
  };

  const handleClearAll = () => {
    setSelectedBanks(new Set());
    setSelectedGroups({});
  };

  return (
    <Box sx={{ p: 2, maxWidth: 720, mx: 'auto' }}>
      {/* Header */}
      <Stack direction="row" sx={{ mb: 3, alignItems: 'center' }}>
        <IconButton size="small" onClick={() => navigate('/home')} sx={{ mr: 1 }}>
          <ArrowBackIosNewRoundedIcon fontSize="small" />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>自定义刷题</Typography>
        {selectedBanks.size > 0 && (
          <Chip
            label={`已选 ${selectedBanks.size} 个题库`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: 26, fontSize: '0.7rem' }}
          />
        )}
      </Stack>

      {/* Bulk actions */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button size="small" variant="outlined" onClick={handleSelectAll} sx={{ borderRadius: 2.5, fontSize: '0.75rem' }}>
          全选题库
        </Button>
        <Button size="small" variant="outlined" onClick={handleClearAll} sx={{ borderRadius: 2.5, fontSize: '0.75rem' }}>
          清除全部
        </Button>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
          共 {totalQuestionsAll} 题可选
        </Typography>
      </Stack>

      {/* Bank & chapter selection */}
      <Stack spacing={1.5} sx={{ mb: 3 }}>
        {bankGroups.map(bg => {
          const bankSelected = selectedBanks.has(bg.bankId);
          const chapterSel = selectedGroups[bg.bankId] || [];
          const isFiltered = chapterSel.length > 0;

          return (
            <Card
              key={bg.bankId}
              elevation={0}
              sx={{
                borderRadius: 4,
                bgcolor: 'background.paper',
                border: '1.5px solid',
                borderColor: bankSelected ? 'primary.main' : 'divider',
                transition: 'border-color 0.2s',
              }}
            >
              <CardContent sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}>
                <Stack direction="row" sx={{ alignItems: 'center' }}>
                  <Checkbox
                    size="small"
                    checked={bankSelected}
                    sx={{ mr: 0.75 }}
                    onChange={() => toggleBank(bg.bankId)}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      flex: 1,
                      color: bankSelected ? 'text.primary' : 'text.secondary',
                    }}
                  >
                    {bg.bankName}
                  </Typography>
                  <Chip
                    label={
                      isFiltered
                        ? `${chapterSel.length}/${bg.groups.length} 章`
                        : `${bg.questionCount} 题`
                    }
                    size="small"
                    color={bankSelected ? 'primary' : 'default'}
                    variant={bankSelected ? 'filled' : 'outlined'}
                    sx={{ height: 22, fontSize: '0.68rem', mr: 0.5 }}
                  />
                </Stack>

                {bg.groups.length > 0 && bankSelected && (
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5, pl: 3.5 }}>
                    <Chip
                      size="small"
                      label={`全部 (${bg.groups.reduce((s, g) => s + g.questionIds.length, 0)}题)`}
                      variant={!isFiltered ? 'filled' : 'outlined'}
                      color={!isFiltered ? 'primary' : 'default'}
                      onClick={() => {
                        setSelectedGroups(prev => {
                          const n = { ...prev };
                          delete n[bg.bankId];
                          return n;
                        });
                      }}
                      sx={{ height: 26, fontSize: '0.68rem', borderRadius: 1.5 }}
                    />
                    {bg.groups.map(g => {
                      const checked = chapterSel.includes(g.id);
                      return (
                        <Chip
                          key={g.id}
                          size="small"
                          label={`${g.name} (${g.questionIds.length})`}
                          variant={checked ? 'filled' : 'outlined'}
                          color={checked ? 'primary' : 'default'}
                          onClick={() => toggleGroup(bg.bankId, g.id)}
                          sx={{ height: 26, fontSize: '0.68rem', borderRadius: 1.5 }}
                        />
                      );
                    })}
                  </Stack>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      <Divider sx={{ my: 3 }} />

      {/* Count */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
        刷题数量 {selectedBanks.size === 0 ? '' : `（已选 ${totalAvailable} 题）`}
      </Typography>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 2 }}>
        <Slider
          value={count} min={1} max={Math.max(1, totalAvailable)} step={1}
          onChange={(_, v) => setCount(v as number)}
          sx={{ flex: 1 }}
          disabled={totalAvailable === 0}
        />
        <TextField
          type="number" size="small" value={count}
          onChange={e => setCount(Math.max(1, Math.min(parseInt(e.target.value) || 1, Math.max(1, totalAvailable))))}
          slotProps={{ htmlInput: { min: 1, max: Math.max(1, totalAvailable), style: { width: 56, textAlign: 'center' } } }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
          disabled={totalAvailable === 0}
        />
      </Stack>

      {/* Mode */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 3, color: 'text.secondary' }}>
        刷题模式
      </Typography>
      <ToggleButtonGroup
        value={mode} exclusive size="small" fullWidth
        onChange={(_, v) => v && setMode(v as ModeOption)}
        sx={{ mb: 2, '& .MuiToggleButton-root': { borderRadius: 2.5, py: 1, fontWeight: 500 } }}
      >
        {(Object.entries(MODE_LABELS) as [ModeOption, string][]).map(([k, v]) => (
          <ToggleButton key={k} value={k}>{v}</ToggleButton>
        ))}
      </ToggleButtonGroup>

      {/* Shuffle */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 3, color: 'text.secondary' }}>
        乱序设置
      </Typography>
      <Stack direction="row" spacing={1.5}>
        <Chip
          label="题目乱序" variant={shuffleQuestions ? 'filled' : 'outlined'} color={shuffleQuestions ? 'primary' : 'default'}
          onClick={() => setShuffleQuestions(v => !v)} sx={{ borderRadius: 2.5, fontWeight: 500 }}
        />
        <Chip
          label="选项乱序" variant={shuffleOptions ? 'filled' : 'outlined'} color={shuffleOptions ? 'primary' : 'default'}
          onClick={() => setShuffleOptions(v => !v)} sx={{ borderRadius: 2.5, fontWeight: 500 }}
        />
      </Stack>

      {/* Start */}
      <Button
        fullWidth variant="contained" size="large"
        startIcon={<PlayArrowRoundedIcon />}
        onClick={handleStart}
        disabled={selectedBanks.size === 0 || totalAvailable === 0}
        sx={{ borderRadius: 3, py: 1.5, mt: 4, fontWeight: 600 }}
      >
        开始刷题 {selectedBanks.size > 0 ? `(${Math.min(count, totalAvailable)} 题)` : ''}
      </Button>

      {/* Session conflict dialog */}
      <Dialog open={showSessionDialog} onClose={() => setShowSessionDialog(false)} maxWidth="xs" fullWidth
        slotProps={{ paper: { sx: { borderRadius: 4 } } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>已有进行中的刷题</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            当前有一个尚未结束的刷题会话，请在下方选择操作以继续刷题
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1, flexWrap: 'wrap', gap: 1 }}>
          <Button
            onClick={() => { setShowSessionDialog(false); navigate('/quiz/active'); }}
            variant="outlined" sx={{ borderRadius: 2.5, flex: 1 }}
          >
            前往当前刷题会话
          </Button>
          <Button
            variant="contained" color="error" sx={{ borderRadius: 2.5, flex: 1 }}
            onClick={() => {
              setShowSessionDialog(false);
              clearSession();
              doStart();
            }}
          >
            结束并继续本次刷题
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
